/**
 * SprintStore — the Sprint Board's sprint data-access wrapper.
 *
 * Strict layering: talks to the API only through the {@link EngineeringVectorApi}
 * facade (`api.documents<Sprint>('sprints')` + `api.identityProvider`). Never
 * imports ApiClient or calls fetch. React hooks call this store; pages call the
 * hook.
 *
 * Persistence model: each sprint is one document with id `sprint_<uuid>`. Every
 * renderable field (roomId, name, dates, teamId, goal, status, createdBy,
 * timestamps, deleted) is mirrored onto the DDB pointer row's index attributes
 * so a room's sprint list renders directly from the pointer rows WITHOUT
 * hydrating S3 bodies.
 *
 * Room scoping: {@link listByRoom} issues a roomId-filtered listMetaInfo query
 * (exactly like TaskStore.listByRoom) and defensively re-filters client-side.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 */

import type { EngineeringVectorApi } from '../../../api';
import type { DocumentStore } from '../../../api';
import type { StringMap } from '../../../api';
import type { ListMetaInfoQuery } from '../../../api';
import type { Sprint, SprintDraft, SprintStatus, CommonTask } from '../models/types';
import { isSprintStatus, sprintDocId } from '../models/sprintboardModel';

export const SPRINTS_COLLECTION = 'sprints';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  roomId: 'roomId',
  name: 'name',
  startDate: 'startDate',
  endDate: 'endDate',
  teamId: 'teamId',
  goal: 'goal',
  status: 'status',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  oncallDaysByAlias: 'oncallDaysByAlias',
  commonTasks: 'commonTasks',
  deleted: 'deleted',
} as const;

export class SprintStore {
  private readonly docs: DocumentStore<Sprint>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Sprint>(SPRINTS_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(s: Sprint): StringMap {
    return {
      [ROW.roomId]: s.roomId,
      [ROW.name]: s.name,
      [ROW.startDate]: s.startDate ?? '',
      [ROW.endDate]: s.endDate ?? '',
      [ROW.teamId]: s.teamId ?? '',
      [ROW.goal]: s.goal ?? '',
      [ROW.status]: s.status,
      [ROW.createdBy]: s.createdBy ?? '',
      [ROW.createdAt]: s.createdAt ?? '',
      [ROW.updatedAt]: s.updatedAt ?? '',
      [ROW.oncallDaysByAlias]: JSON.stringify(s.oncallDaysByAlias ?? {}),
      [ROW.commonTasks]: JSON.stringify(s.commonTasks ?? []),
      [ROW.deleted]: s.deleted ? 'true' : 'false',
    };
  }

  /** Reconstructs a full Sprint from a pointer row. */
  private toSprint(row: StringMap): Sprint {
    const status = row[ROW.status] ?? '';
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      roomId: row[ROW.roomId] ?? '',
      name: row[ROW.name] ?? '',
      startDate: row[ROW.startDate] ?? '',
      endDate: row[ROW.endDate] ?? '',
      teamId: row[ROW.teamId] || undefined,
      goal: row[ROW.goal] || undefined,
      status: (isSprintStatus(status) ? status : 'planning') as SprintStatus,
      createdBy: row[ROW.createdBy] ?? '',
      createdAt: row[ROW.createdAt] ?? '',
      updatedAt: row[ROW.updatedAt] ?? '',
      oncallDaysByAlias: this.parseOncallAttr(row[ROW.oncallDaysByAlias]),
      commonTasks: this.parseCommonTasksAttr(row[ROW.commonTasks]),
      deleted: row[ROW.deleted] === 'true',
    };
  }

  /** Safely parses the JSON-encoded on-call map (alias → non-negative days). */
  private parseOncallAttr(raw: string | undefined): Record<string, number> {
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      const out: Record<string, number> = {};
      for (const [alias, value] of Object.entries(parsed)) {
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
          out[alias] = value;
        }
      }
      return out;
    } catch {
      return {};
    }
  }

  /** Safely parses the JSON-encoded common-tasks list. */
  private parseCommonTasksAttr(raw: string | undefined): CommonTask[] {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(
          (t) =>
            t &&
            typeof t === 'object' &&
            typeof t.id === 'string' &&
            typeof t.name === 'string',
        )
        .map((t) => ({
          id: t.id,
          name: t.name,
          daysPerMember:
            typeof t.daysPerMember === 'number' &&
            Number.isFinite(t.daysPerMember) &&
            t.daysPerMember > 0
              ? t.daysPerMember
              : 0,
        }));
    } catch {
      return [];
    }
  }

  /**
   * Lists a room's non-deleted sprints. Issues a roomId-filtered listMetaInfo
   * query, then re-filters client-side (collection + roomId + not-deleted) so
   * the result is correct even if the backend ignores the filter expression.
   */
  async listByRoom(roomId: string): Promise<Sprint[]> {
    if (!roomId) return [];
    const query: ListMetaInfoQuery = {
      filterExpression: '#roomId = :roomId AND #deleted <> :deleted',
      expressionAttributeNames: {
        '#roomId': ROW.roomId,
        '#deleted': ROW.deleted,
      },
      expressionAttributeValues: {
        ':roomId': roomId,
        ':deleted': 'true',
      },
    };
    const rows = await this.docs.list(query);
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === SPRINTS_COLLECTION &&
          r[ROW.roomId] === roomId &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSprint(r));
  }

  /** Loads a single sprint document by id. */
  async get(id: string): Promise<Sprint | undefined> {
    return this.docs.get(id);
  }

  /**
   * Lists ALL non-deleted sprints across every room. Used by cross-room views
   * (e.g. the AI Team Digest's sprint-execution section) that reason over the
   * whole workspace independent of the active room. Filters to this collection
   * + not-deleted defensively.
   */
  async listAll(): Promise<Sprint[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === SPRINTS_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSprint(r));
  }

  /** Creates a new sprint in a room. Seeds id/createdBy/timestamps. */
  async create(roomId: string, draft: SprintDraft): Promise<Sprint> {
    const identity = await this.api.identityProvider.getIdentity();
    const now = new Date().toISOString();
    const sprint: Sprint = {
      id: sprintDocId(),
      roomId,
      name: draft.name.trim(),
      startDate: draft.startDate,
      endDate: draft.endDate,
      teamId: draft.teamId?.trim() || undefined,
      goal: draft.goal?.trim() || undefined,
      status: draft.status,
      createdBy: identity.alias,
      createdAt: now,
      updatedAt: now,
      oncallDaysByAlias: {},
      commonTasks: [],
      deleted: false,
    };
    await this.docs.save(sprint.id, sprint, this.indexAttributes(sprint));
    return sprint;
  }

  /** Applies a draft's editable fields to an existing sprint in place. */
  async applyDraft(sprint: Sprint, draft: SprintDraft): Promise<Sprint> {
    const updated: Sprint = {
      ...sprint,
      name: draft.name.trim(),
      startDate: draft.startDate,
      endDate: draft.endDate,
      teamId: draft.teamId?.trim() || undefined,
      goal: draft.goal?.trim() || undefined,
      status: draft.status,
      updatedAt: new Date().toISOString(),
      deleted: false,
    };
    return this.update(updated);
  }

  /** Persists a full sprint document in place. */
  async update(sprint: Sprint): Promise<Sprint> {
    const updated: Sprint = { ...sprint, deleted: false };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Persists the sprint's capacity bandwidth inputs — the manually-entered
   * on-call days per member and the common / all-hands tasks — in one write.
   * Either field may be omitted to leave it unchanged. Updates `updatedAt` and
   * re-mirrors the index attributes.
   */
  async setBandwidth(
    sprint: Sprint,
    bandwidth: {
      oncallDaysByAlias?: Record<string, number>;
      commonTasks?: CommonTask[];
    },
  ): Promise<Sprint> {
    return this.update({
      ...sprint,
      oncallDaysByAlias:
        bandwidth.oncallDaysByAlias ?? sprint.oncallDaysByAlias ?? {},
      commonTasks: bandwidth.commonTasks ?? sprint.commonTasks ?? [],
      updatedAt: new Date().toISOString(),
    });
  }

  /** Transitions a sprint to a new lifecycle status. */
  async setStatus(sprint: Sprint, status: SprintStatus): Promise<Sprint> {    if (sprint.status === status) return sprint;
    return this.update({
      ...sprint,
      status,
      updatedAt: new Date().toISOString(),
    });
  }

  /** Soft-deletes a sprint (flags the document + pointer row). */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Sprint = {
      ...existing,
      deleted: true,
      updatedAt: new Date().toISOString(),
    };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }
}
